import { DEFAULT_SHORTCUTS } from "@/lib/keyboardShortcuts";
import { useChartStore } from "@/stores/chartStore";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import type { ChartTypeValue } from "@/lib/lytick/chart-config";
import type { WorkspaceTemplate } from "@/types/workspace";
import { getCatalogEntry } from "@/lib/lytick/indicatorRegistry";
import type { IndicatorInstance } from "@/types/indicator";

export const WORKSPACE_TEMPLATES_STORAGE_KEY = "lytick-workspace-templates";

export const DEFAULT_TEMPLATES: WorkspaceTemplate[] = [
  {
    id: "default",
    name: "Default",
    icon: "DF",
    description: "Clean chart starting point",
    layout: "single",
    chartType: "candle_solid",
    timeframe: "5m",
    activeIndicators: [],
    selectedSymbols: [],
    isDefault: true,
  },
];

export function loadCustomTemplates(): WorkspaceTemplate[] {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const rawTemplates = window.localStorage.getItem(WORKSPACE_TEMPLATES_STORAGE_KEY);

    if (!rawTemplates) {
      return [];
    }

    return (JSON.parse(rawTemplates) as WorkspaceTemplate[]).map((template) => ({
      ...template,
      isDefault: false,
    }));
  } catch {
    return [];
  }
}

export function getWorkspaceTemplates() {
  return [...DEFAULT_TEMPLATES, ...loadCustomTemplates()];
}

export function saveCustomTemplate(template: WorkspaceTemplate) {
  if (typeof window === "undefined") {
    return false;
  }

  const customTemplates = loadCustomTemplates();
  const normalizedName = template.name.trim().toLowerCase();

  if (!normalizedName) {
    return false;
  }

  const nextTemplates = customTemplates.filter(
    (item) => item.id !== template.id && item.name.trim().toLowerCase() !== normalizedName,
  );

  nextTemplates.push({
    ...template,
    isDefault: false,
  });

  window.localStorage.setItem(
    WORKSPACE_TEMPLATES_STORAGE_KEY,
    JSON.stringify(nextTemplates),
  );
  window.dispatchEvent(new CustomEvent("lytick:templates-updated"));
  return true;
}

export function deleteCustomTemplate(templateId: string) {
  if (typeof window === "undefined") {
    return;
  }

  const nextTemplates = loadCustomTemplates().filter((template) => template.id !== templateId);
  window.localStorage.setItem(
    WORKSPACE_TEMPLATES_STORAGE_KEY,
    JSON.stringify(nextTemplates),
  );
  window.dispatchEvent(new CustomEvent("lytick:templates-updated"));
}

export function buildWorkspaceTemplate(name: string): WorkspaceTemplate {
  const chartState = useChartStore.getState();

  return {
    id: `custom-${Date.now()}`,
    name: name.trim(),
    icon: name.slice(0, 2).toUpperCase(),
    description: "Custom workspace template",
    layout: "single",
    chartType: chartState.chartType,
    timeframe: chartState.timeframe,
    activeIndicators: [...chartState.indicators],
    selectedSymbols: [chartState.symbol],
    isDefault: false,
  };
}

export function applyWorkspaceTemplate(template: WorkspaceTemplate) {
  const chartStore = useChartStore.getState();

  chartStore.setChartType(template.chartType);
  chartStore.setTimeframe(template.timeframe);

  useChartStore.setState({
    indicators: [...template.activeIndicators] as IndicatorInstance[],
  });

  if (template.selectedSymbols[0]) {
    chartStore.setSymbol(template.selectedSymbols[0], template.selectedSymbols[0]);
  }

  useWorkspaceStore.getState().setActiveTemplateId(template.id);
}

export function getTemplateShortcutTargets() {
  return getWorkspaceTemplates().slice(0, 3);
}

export function resetShortcutTemplatesFallback() {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(
    new CustomEvent("lytick:shortcuts-updated", {
      detail: DEFAULT_SHORTCUTS,
    }),
  );
}
